import { useMemo } from "react";
import { PencilSimple, Trash } from "@phosphor-icons/react";

const formatMoney = (value) => {
  return new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    minimumFractionDigits: 2,
  }).format(value || 0);
};

export const ReportLedgerTable = ({
  canManageExpenses,
  onDeleteExpense,
  onDeleteProfit,
  onEditExpense,
  onEditProfit,
  report,
}) => {
  const rows = useMemo(() => {
    return report.transactions.map((transaction, index) => ({
      ...transaction,
      rowId: `${transaction.date}-${transaction.customer}-${index}`,
      testId: `report-transaction-${index}`,
    }));
  }, [report.transactions]);

  const profitTone = report.profit_loss < 0 ? "report-debit" : "report-credit";

  return (
    <section className="report-ledger-table-wrap">
      <table data-testid="report-ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Agent ID</th>
            <th>Customer</th>
            <th>Description</th>
            <th className="amount">Debit</th>
            <th className="amount">Credit</th>
            <th className="amount">Expenses</th>
            <th className="amount">Balance</th>
            {canManageExpenses && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const balanceTone = item.balance < 0 ? "report-debit" : "report-credit";
            return (
              <tr
                key={item.rowId}
                data-testid={item.testId}
                className={item.status === "void" ? "void-transaction" : ""}
              >
                <td>{item.date}</td>
                <td>{item.agent}</td>
                <td><strong>{item.customer}</strong></td>
                <td className="report-description">{item.description}</td>
                <td className="amount report-debit">
                  {item.debit ? formatMoney(item.debit) : "—"}
                </td>
                <td className="amount report-credit">
                  {item.credit ? formatMoney(item.credit) : "—"}
                </td>
                <td className="amount report-expense">
                  {item.expense ? formatMoney(item.expense) : "—"}
                </td>
                <td className={`amount ${balanceTone}`}>{formatMoney(item.balance)}</td>
                {canManageExpenses && (
                  <td>
                    {item.status === "expense" && item.expense_id && (
                      <div className="report-row-actions">
                        <button
                          className="customer-icon-button"
                          onClick={() => onEditExpense(item)}
                          data-testid={`edit-expense-${item.expense_id}`}
                          aria-label={`Edit expense ${item.description}`}
                        >
                          <PencilSimple size={17} />
                        </button>
                        <button
                          className="customer-icon-button customer-delete-button"
                          onClick={() => onDeleteExpense(item)}
                          data-testid={`delete-expense-${item.expense_id}`}
                          aria-label={`Delete expense ${item.description}`}
                        >
                          <Trash size={17} />
                        </button>
                      </div>
                    )}
                    {item.status === "profit" && item.profit_id && (
                      <div className="report-row-actions">
                        <button
                          className="customer-icon-button"
                          onClick={() => onEditProfit(item)}
                          data-testid={`edit-profit-${item.profit_id}`}
                          aria-label={`Edit profit ${item.description}`}
                        >
                          <PencilSimple size={17} />
                        </button>
                        <button
                          className="customer-icon-button customer-delete-button"
                          onClick={() => onDeleteProfit(item)}
                          data-testid={`delete-profit-${item.profit_id}`}
                          aria-label={`Delete profit ${item.description}`}
                        >
                          <Trash size={17} />
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={canManageExpenses ? "5" : "4"}><strong>Total</strong></td>
            <td className="amount report-debit">{formatMoney(report.cash_disbursed)}</td>
            <td className="amount report-credit">{formatMoney(report.collections)}</td>
            <td className="amount report-expense">{formatMoney(report.expenses)}</td>
            <td className={`amount ${profitTone}`}>{formatMoney(report.profit_loss)}</td>
          </tr>
        </tfoot>
      </table>
      {!rows.length && (
        <div className="empty-state" data-testid="report-empty-state">
          No transactions for this period.
        </div>
      )}
    </section>
  );
};